import { ForwardedRef, forwardRef, useMemo } from "react"
import { useGetRunsNodesResultsHistoricAggregateQuery } from "@store/services/runService"
import { Box, CircularProgress, Grid, SxProps, Typography } from "@mui/material"
import { Line } from "react-chartjs-2"
import { color_tones, lighter_rgba, to_rgba } from "@features/results/Result/const"
import ChartDataLabels from "chartjs-plugin-datalabels"
import zoomPlugin from "chartjs-plugin-zoom"
import { Chart, ChartData, ChartOptions, Point } from "chart.js"
import { Tooltip } from "chart.js"
import { AggregateFunctionName, RunDto } from "@custom-types/modelRun"
import { calculateLagShift } from "utils/run"
import { getResults, getTimesteps } from "@features/results/Result/AggregateFunctionChart/helpers"
import { graphViewGroupNodeLabelToSampleNode } from "@features/graph/utils/graphUtils"
import { GroupNodeLabel } from "@features/graph/graphViewModel"
import { skipToken } from "@reduxjs/toolkit/dist/query/react"
import { formatValuePythonic } from "@utils/format/pythonicFormat"
import { generateChartLabel } from "@utils/chartLabelUtils"

Chart.register(zoomPlugin)

type AggregateFunctionChartProps = {
  runs: RunDto[]
  node: GroupNodeLabel | null
  aggregationFunc: AggregateFunctionName[]
  customLabels?: string[]
  maintainAspectRatio?: boolean
  sx?: SxProps
  chartContainerSx?: SxProps
  legendPosition?: "top" | "bottom"
  showLegend?: boolean
  hiddenDatasets?: string[]
  onLegendClick?: (datasetLabel: string) => void
}

export type AggregatedResult = { key: string; data: (number | null)[]; timesteps: string[] }

const almost = (a: number, b: number, delta = 0.000001) => Math.abs(a - b) < delta

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
const up = function (context: any) {
  return context.dataset.data[context.dataIndex - 1] < context.dataset.data[context.dataIndex]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
const equiv = function (context: any) {
  return almost(
    context.dataset.data[context.dataIndex - 1],
    context.dataset.data[context.dataIndex],
  )
}

// @ts-ignore
Tooltip.positioners.customPositioner = function (elements: unknown, eventPosition: { x: number }) {
  return {
    x: eventPosition.x,
    y: 0,
  }
}

export const AggregateFunctionChart = forwardRef(function AggregateFunctionChart(
  {
    runs,
    node,
    aggregationFunc,
    customLabels = [],
    maintainAspectRatio = false,
    sx,
    chartContainerSx = { height: 500, my: 2 },
    legendPosition = "top",
    showLegend = true,
    hiddenDatasets = [],
    onLegendClick,
  }: AggregateFunctionChartProps,
  chartRef: ForwardedRef<Chart<"line", (number | Point | null)[], unknown> | undefined> | undefined,
) {
  const runKeys = runs.map((run) => run.key)
  const { isFetching, data: aggregateResultsResponse } =
    useGetRunsNodesResultsHistoricAggregateQuery(
      node
        ? {
            runKeys,
            nodes: [graphViewGroupNodeLabelToSampleNode(node?.label)],
          }
        : skipToken,
    )
  const lagShift = useMemo(
    () => calculateLagShift(aggregateResultsResponse?.results),
    [aggregateResultsResponse?.results],
  )

  const labels = useMemo<string[]>(
    () => getTimesteps(aggregateResultsResponse?.results, lagShift),
    [aggregateResultsResponse?.results, lagShift],
  )

  const results = useMemo<AggregatedResult[][]>(
    () => getResults(aggregateResultsResponse?.results, lagShift, aggregationFunc),
    [aggregateResultsResponse?.results, lagShift, aggregationFunc],
  )

  const chartOptions: ChartOptions<"line"> = {
    responsive: true,
    scales: {
      x: {
        min: labels[0] || undefined,
        max: labels[labels.length - 1] || undefined,
      },
      y: {
        ticks: {
          callback: function (value: number | string) {
            return formatValuePythonic(value, node?.format)
          },
        },
      },
    },
    plugins: {
      zoom: {
        limits: {
          x: { minRange: 2 },
        },
        zoom: {
          drag: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: "x",
        },
      },
      legend: {
        position: legendPosition,
        display: showLegend,
        labels: {
          boxWidth: 11,
          boxHeight: 11,
          font: {
            size: 11,
          },
        },
        onClick: onLegendClick
          ? function (event, legendItem, legend) {
              const index = legendItem.datasetIndex
              if (index === undefined) return
              const dataset = legend.chart.data.datasets[index]
              if (dataset?.label) {
                onLegendClick(dataset.label)
              }
            }
          : undefined,
      },
      tooltip: {
        caretSize: 0,
        enabled: true,
        // @ts-ignore TODO kk: fix type
        position: "customPositioner",
      },
      datalabels: {
        display: true,
        align: function () {
          return "end"
        },
        offset: 0,
        font: {
          size: 8,
        },
        formatter: function (value: string, context) {
          return context.active
            ? (context.dataset.label ?? "").split("-").slice(-1) +
                ":" +
                parseFloat(value).toFixed(3)
            : up(context)
            ? "▲"
            : equiv(context)
            ? "="
            : "▼"
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
        color: function (context: any) {
          return context.dataIndex - 1 >= 0
            ? up(context)
              ? "green"
              : equiv(context)
              ? "gray"
              : "red"
            : "gray"
        },
        backgroundColor: function (context) {
          return context.active ? "rgba(255,255,255,0.6)" : null
        },
        borderWidth: function (context) {
          return context.active ? 2 : 0
        },
        borderRadius: 4,
        borderColor: "gray",
        labels: {
          title: {
            font: {
              weight: "bold",
            },
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
    borderWidth: 1,
    borderRadius: 10,
    tension: 0.3,
    maintainAspectRatio,
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  const dataChart: any = useMemo<ChartData>(
    () => ({
      labels,
      datasets: results.reduce<ChartData["datasets"]>(
        (acc: ChartData["datasets"], aggregatedData: AggregatedResult[], currentIndex: number) => {
          runKeys.forEach((_, itemIndex) => {
            const aggregate = aggregatedData[itemIndex]
            if (!aggregate) return

            const data: AggregatedResult["data"] = []
            const colorTone = color_tones[itemIndex] ?? []

            labels.forEach((label) => {
              const foundItem = aggregate.timesteps.indexOf(label) ?? -1
              data.push(foundItem !== -1 ? aggregate.data[foundItem] ?? null : 0)
            })

            const currentRun = runs.find((run) => run.key === runKeys[itemIndex])

            const label = generateChartLabel({
              nodeName: customLabels[itemIndex] ?? node?.label ?? "",
              nodeUnit: node?.format,
              runName: currentRun?.run_name ?? undefined,
              runVersion: currentRun?.version ?? undefined,
              numberOfRuns: runs.length,
              statisticalSuffix: aggregate.key,
            })
            acc.push({
              label,
              data,
              backgroundColor: to_rgba(lighter_rgba(colorTone, 0, 0.5)),
              borderDash: currentIndex
                ? [(currentIndex + 1) * 3, (currentIndex + 1) * 2]
                : undefined,
              borderWidth: 3,
              borderColor: to_rgba(lighter_rgba(colorTone, 0, 1)),
            })
          })

          return acc
        },
        [],
      ),
    }),
    [results, labels, runKeys, runs, node, customLabels],
  )

  // Apply hidden state to datasets based on hiddenDatasets prop
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  dataChart.datasets.forEach((dataset: any) => {
    if (dataset.label) {
      dataset.hidden = hiddenDatasets.includes(dataset.label)
    }
  })

  return (
    <Grid item xs={12} sx={sx}>
      {results.length === 0 && !isFetching ? (
        <Typography variant="h6" sx={{ textAlign: "center", my: 4 }}>
          Please select node and aggregation function to see comparison table and bar chart
        </Typography>
      ) : isFetching ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={chartContainerSx}>
          <Line
            options={chartOptions}
            plugins={[ChartDataLabels]}
            data={dataChart}
            ref={chartRef}
          />
        </Box>
      )}
    </Grid>
  )
})
